<template>
  <div
    :style="{
      '--plp-grid-count-mobile': gridSizeMobile,
      '--plp-grid-count-desktop': gridSizeDesktop,
    }"
    class="relative grid auto-rows-[minmax(220px,auto)] grid-cols-[repeat(var(--plp-grid-count-mobile,1),minmax(0,1fr))] gap-3.5 gap-y-5 py-6.5 sm:auto-rows-[minmax(360px,auto)] md:auto-rows-[minmax(250px,auto)] md:grid-cols-[repeat(var(--plp-grid-count-desktop,2),minmax(0,1fr))] md:gap-x-4 md:gap-y-6.5 lg:auto-rows-[minmax(310px,auto)] xl:auto-rows-[minmax(360px,auto)]"
  >
    <div
      v-for="(item, idx) in catalogWithBanner"
      :key="item.slug || `banner-${idx}`"
      :class="[
        item.isBanner && item.promotion_banner_options
          ? getBannerClass(item.promotion_banner_options)
          : '',
      ]"
    >
      <component
        :is="item.isBanner ? BlocksBlockPlpPromoted : CommonProductCardPlp"
        v-bind="
          item.isBanner
            ? {
                position: item.promotion_banner_options?.position,
                redirectTo: item.promotion_banner_options?.url,
                imgFile: item.promotion_banner_file,
                text: item.currentTranslation?.promotion_banner_text,
              }
            : {
                product: item,
                'product-index': idx,
                'statistic-item-list-name':
                  pageData?.currentTranslation?.title || t('catalog'),
                'plp-type': plpType,
              }
        "
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  PageData,
  CatalogData,
  DiscountData,
  DiscountPage,
  PromotionBannerOptions,
} from "ecom-types";
import { bannerSize } from "@/configs";
import { CommonProductCardPlp, BlocksBlockPlpPromoted } from "#components";

const props = defineProps<{
  dataCatalog: CatalogData[];
  plpType?: string;
  pageData: PageData | null;
  gridSizeDesktop: number;
  gridSizeMobile: number;
}>();

const route = useRoute();
const generalStore = useGeneralStore();
const { t } = useI18n();
const { $api } = useNuxtApp();
const { mdAndUp } = useAppBreakpoints();

const bannerParams = ref<DiscountData[] | null>(null);
const itemsOnPage = 8;

const { data: dataDiscount } = await useAsyncCachedData("discount-data", () => {
  return $api.general.getDiscountCampaigns();
});

const discountData = ref(dataDiscount.value?.data);

const banners = computed(() =>
  discountData.value?.filter((item: DiscountData) => item.promotion_banner),
);

const catalogWithBanner = computed(() => {
  if (!bannerParams.value) {
    return [...props.dataCatalog];
  }

  const catalog = [...props.dataCatalog];

  (bannerParams?.value as DiscountData[])?.forEach((banner: DiscountData) => {
    if (catalog.length > banner.insertIndex) {
      catalog.splice(banner.insertIndex, 0, {
        isBanner: true,
        ...banner,
      } as any);
    }
  });

  return catalog;
});

watch(
  () => [props.gridSizeDesktop, props.gridSizeMobile, props.dataCatalog],
  () => {
    calculateBannerParams();
  },
);

onMounted(() => {
  window.addEventListener("resize", calculateBannerParams);
  calculateBannerParams();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", calculateBannerParams);
});

function getBannerClass(banner: PromotionBannerOptions) {
  const sizeClassMap = {
    [bannerSize["1x1"]]: "col-span-1 row-span-1",
    [bannerSize["1x2"]]: "col-span-2 row-span-1",
    [bannerSize["2x2"]]: "col-span-2 row-span-2",
    [bannerSize["1x4"]]: "col-span-4 row-span-1 hidden md:block",
  };
  return `${sizeClassMap[banner.size]}`;
}

function parsePageUrl(pageUrl: string) {
  const url = new URL(pageUrl, generalStore.settings?.front_domain);
  const route = url.pathname;
  const pageNumber = Number(url.searchParams.get("page") || 1);
  return {
    route,
    pageNumber,
  };
}

function calculateBannerParams() {
  if (banners?.value?.length === 0) {
    return;
  }

  let totalOffset: number = 0;
  let totalBannerCount: number = 0;
  const totalLoadedPages = Math.ceil(props.dataCatalog.length / itemsOnPage);
  const pageIds: number[] = [];
  const bannerParamsTemp: DiscountData[] = [];

  const currentRoutePath = route.path.split("/").filter(Boolean);
  const currentRoutePage = Number(route.query.page) || 1;

  const skipMultiplier = mdAndUp.value
    ? props.gridSizeDesktop === 4
      ? 4
      : 2
    : props.gridSizeMobile === 2
      ? 2
      : 1;

  banners?.value?.forEach((banner: DiscountData) => {
    banner.pages.forEach((page: DiscountPage) => {
      const { route, pageNumber } = parsePageUrl(page.page_link);
      if (currentRoutePath.includes(route.replace("/", ""))) {
        pageIds.push(pageNumber);
      }
    });
  });

  pageIds.sort((a: number, b: number) => a - b);

  const uniquePageIds = new Set(pageIds);
  const topSlicePage =
    (currentRoutePage * itemsOnPage - props.dataCatalog.length) / itemsOnPage;

  for (const id of uniquePageIds) {
    if (id <= topSlicePage) {
      uniquePageIds.delete(id);
    } else if (id > currentRoutePage) {
      uniquePageIds.delete(id);
    }
  }

  uniquePageIds.forEach((pageId: number) => {
    let offset = 0;
    let firstAdjustment = 0;
    let isNewPage = true;
    let isFirstElement = true;

    banners?.value
      ?.sort(
        (a: DiscountData, b: DiscountData) =>
          a.promotion_banner_options?.skip_rows -
          b.promotion_banner_options?.skip_rows,
      )
      .filter(
        (banner: DiscountData) =>
          skipMultiplier === 4 ||
          banner.promotion_banner_options?.size !== bannerSize["1x4"],
      )
      .filter((banner: DiscountData) =>
        skipMultiplier === 1
          ? banner.promotion_banner_options?.size === bannerSize["1x1"]
          : true,
      )
      .forEach((banner: DiscountData) => {
        if (
          !banner.pages.some((page: DiscountPage) => {
            const { route, pageNumber } = parsePageUrl(page.page_link);
            return (
              pageNumber === pageId &&
              currentRoutePath.includes(route.replace("/", ""))
            );
          })
        ) {
          return;
        }

        // Page start index
        const pageStartIndexBase =
          itemsOnPage * (currentRoutePage - (currentRoutePage - pageId) - 1) +
          totalBannerCount -
          (currentRoutePage - totalLoadedPages) * itemsOnPage;

        const elementsOnCurrentPage =
          itemsOnPage * (pageId - currentRoutePage) + totalOffset;
        const remainder = elementsOnCurrentPage % skipMultiplier;
        const adjustment = remainder === 0 ? 0 : skipMultiplier - remainder;

        if (isNewPage && pageId !== 1) {
          firstAdjustment = adjustment;
        }

        const pageStartIndex =
          isNewPage && pageId !== 1
            ? pageStartIndexBase + adjustment
            : pageStartIndexBase;
        isNewPage = false;

        // Calculate page insert index
        const bannerPosition = banner.promotion_banner_options?.position;
        const bannerSkipRows = banner.promotion_banner_options?.skip_rows;
        const [columnsOccupied, rowsOccupied] =
          banner.promotion_banner_options?.size.split("x").map(Number);
        let pageInsertIndex: any;

        if (
          bannerPosition === 0 ||
          (bannerPosition === 1 && rowsOccupied === 4)
        ) {
          pageInsertIndex =
            pageStartIndex + bannerSkipRows * skipMultiplier - offset;
        } else if (bannerPosition === 1) {
          pageInsertIndex =
            pageStartIndex +
            bannerSkipRows * skipMultiplier -
            offset +
            (skipMultiplier - rowsOccupied);
        }

        if (!isFirstElement) {
          pageInsertIndex += firstAdjustment;
        }

        bannerParamsTemp.push({
          ...banner,
          insertIndex: pageInsertIndex,
        });

        offset += rowsOccupied * columnsOccupied;
        totalOffset += rowsOccupied * columnsOccupied;
        totalBannerCount++;
        isFirstElement = false;
      });
  });

  bannerParams.value = bannerParamsTemp;
}
</script>
